import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import { createPersona, updatePersona } from "../api/RBCAPI";
import { useAuth } from "../../../inicio/context/AuthContext";
import Swal from "sweetalert2";
import { useOnlineStatus } from "../../../inicio/hooks/useSync";
import { guardarOfflineDiscapacidad } from "../offline/discapacidadOffline";
import { manejarErrorDuplicado } from "../utils/handleDuplicado";
import { obtenerTerritorios, obtenerAatis, obtenerComunidades, obtenerEapbs, obtenerEtnias } from "../../../inicio/offline/listasCache";

// ─── Constantes ───────────────────────────────────────────────────────────────

const TIPOS_DOC_PERSONA = [
  "Certificado de Nacido Vivo","R.C","T.I","C.C","C.E",
  "Pasaporte","Carnet diplomático","Permiso especial de permanencia"
];
const TIPOS_DOC_ACOMPANANTE = [
  "Tarjeta de identidad","C.C","C.E","Pasaporte","Permiso especial de permanencia"
];
const PARENTESCOS = [
  "Padre o Madre","Hijo(a)","Hermano(a)","Esposo(a) / compañero(a)",
  "Abuelo(a)","Otros familiares","No existe parentesco","Persona de apoyo","Representante legal"
];
const ETNIAS = ["Indígena","Afrocolombiano","Rrom - Gitano","Palenquero","Raizal","Ninguno"];
// CULTURAS_INDIGENAS ya no es una lista fija — sale del catálogo `etnias`
// (GET /listas/etnias) y solo se muestra cuando ETNIA === "Indígena".
const ORIENTACIONES = ["Heterosexual","Homosexual","Bisexual","Transgenero","Intersexual","Ninguno"];

const DIFICULTADES_DIARIAS = [
  "Pensar y memorizar",
  "Percibir la luz, distinguir objetos / o usando lentes o gafas",
  "Oir, aun con aparatos especiales",
  "Caminar, correr, saltar, cambiar / o mantener posiciones del cuerpo",
  "Masticar, tragar, asimilar y transformar los alimentos",
  "Relacionarse con las demás personas y su entorno",
  "Hablar y comunicarse",
  "Ninguno",
];
const PRODUCTOS_APOYO = [
  "Productos de apoyo para la movilidad (bastón, muletas, caminador, silla de ruedas)",
  "Órtesis y/o prótesis (en brazos o piernas)",
  "Bastón de orientación (personas ciegas o con baja visión)",
  "Productos de apoyo para la visión (gafas para baja visión, telescopios)",
  "Productos de apoyo para la audición (trompetillas, audífonos, implantes)",
  "Ninguno",
];
const AYUDAS_TECNICAS = [
  "Sillas de ruedas, caminador",
  "Bastón de orientación (personas ciegas o baja visión)",
  "Prótesis y/o Órtesis (en brazos o piernas)",
  "Bastón, muletas",
  "Audífonos, implantes",
  "Silla coche",
  "Ninguno",
];
const CATEGORIAS_DISCAPACIDAD = [
  "Discapacidad Física","Discapacidad Auditiva","Discapacidad Visual",
  "Discapacidad Múltiple","Sordoceguera","Discapacidad Intelectual","Discapacidad Psicosocial"
];

const TOTAL_PASOS = 10;

const parseFecha = (fechaStr) => {
  if (!fechaStr) return { dia: "", mes: "", anio: "" };
  const [anio, mes, dia] = fechaStr.split("T")[0].split("-");
  return { dia: parseInt(dia,10).toString(), mes: parseInt(mes,10).toString(), anio };
};

const calcularEdad = (dia, mes, anio) => {
  if (!dia || !mes || !anio) return { anios: 0, meses: 0 };
  const nac  = new Date(parseInt(anio), parseInt(mes)-1, parseInt(dia));
  const hoy  = new Date();
  let anios  = hoy.getFullYear() - nac.getFullYear();
  let meses  = hoy.getMonth()    - nac.getMonth();
  if (meses < 0) { anios--; meses += 12; }
  if (hoy.getDate() < nac.getDate()) meses--;
  if (meses < 0) { anios--; meses += 12; }
  return { anios: Math.max(0, anios), meses: Math.max(0, meses) };
};

// ─── Formulario inicial ───────────────────────────────────────────────────────
// Sin territorio: municipio/nombre_comunidad son texto libre, no se
// autocompletan desde un selector global.
const initialForm = {
  entidad: "Secretaría de Salud del Amazonas",
  departamento: "AMAZONAS",
  municipio: "",
  asociacion_indigena: "",
  fecha_dia: "", fecha_mes: "", fecha_anio: "",
  tipo_documento: "", documento: "",
  primer_nombre: "", segundo_nombre: "",
  primer_apellido: "", segundo_apellido: "",
  fecha_nac_dia: "", fecha_nac_mes: "", fecha_nac_anio: "",
  edad_anios: "0", edad_meses: "0",
  sexo: "", diagnostico: "",
  afiliado_a: "", sisben_no: "", sisben_nivel: "",
  peso: "", talla: "", imc: "",
  alimentos_complementarios: "", utiliza_paniales: "", eps_suministra_paniales: "",
  direccion: "", barrio: "", area: "", nombre_comunidad: "", corregimiento: "",
  latitud: "", longitud: "", precision_gps: "",
  telefono: "", correo: "",
  acompanantes: [
    { primer_nombre:"",segundo_nombre:"",primer_apellido:"",segundo_apellido:"",tipo_doc:"",numero_doc:"",parentesco:"",calidad:"" },
    { primer_nombre:"",segundo_nombre:"",primer_apellido:"",segundo_apellido:"",tipo_doc:"",numero_doc:"",parentesco:"",calidad:"" },
  ],
  etnia: "", cultura_indigena: "", cultura_indigena_otro: "", orientacion_sexual: "",
  dificultades:              [{ descripcion: [] }],
  productos_apoyos:          [{ descripcion: [] }],
  ayudas_tecnicas:           [{ descripcion: [] }],
  categorias_discapacidades: [{ descripcion: [] }],
  salud_promocion_eps:"", salud_programas_eps:"", salud_prevencion_conoce:"",
  salud_seguridad_orientacion:"", salud_atencion_discapacidad:"", salud_valoracion_interdisciplinar:"",
  salud_remision_pendiente:"", salud_rehabilitacion_eps:"", salud_orientacion_familia:"",
  salud_dispositivo_ordenado:"", salud_entrenamiento_dispositivo:"", salud_entrega_dispositivo:"",
  educacion_sabe_leer:"", educacion_asiste:"", educacion_responde_necesidades:"", educacion_ultimo_nivel:"",
  trabajo_actualmente:"", trabajo_apoyo:"", trabajo_capacidad_afectada:"",
  participacion_actividades:"", participacion_decisiones:"", participacion_conoce_derechos:"",
  entorno_barreras_fisicas:"", entorno_actitudes_negativas:"",
  entorno_plan_casero:"", cuidado_requiere_ayuda:"", cuidado_barreras_vivienda:"",
  rlcpcd_primera_vez: false, rlcpcd_actualizacion: false, rlcpcd_certificado: false,
  entrevistadores: [
    { nombres_apellidos:"", profesion_cargo:"", numero_documento:"" },
    { nombres_apellidos:"", profesion_cargo:"", numero_documento:"" },
  ],
  cuidador_nombres:"", cuidador_documento:"",
  solicitante_nombres:"", solicitante_documento:"",
  gestor:"", eps:"",
};

// ─── Componente ───────────────────────────────────────────────────────────────
export default function DiscapacidadModal({ show, onClose, reload, persona }) {
  const { user } = useAuth();
  const online   = useOnlineStatus();

  const [step,    setStep]    = useState(1);
  const [form,    setForm]    = useState(initialForm);
  const [errores, setErrores] = useState({});
  const [loading, setLoading] = useState(false);
  const [obteniendoGps, setObteniendoGps] = useState(false);
  const [errorGps, setErrorGps] = useState("");

  // ── Catálogos (territorios, AATI, comunidades, EPS/EAPB, etnias) ────────────
  const [territorios, setTerritorios] = useState([]);
  const [aatisTodas,  setAatisTodas]  = useState([]);
  const [comunidadesTodas, setComunidadesTodas] = useState([]);
  const [eapbs,        setEapbs]      = useState([]);
  const [etniasDb,     setEtniasDb]   = useState([]);

  // Se cargan TODAS de una vez al abrir el modal — incluida AATI/comunidad
  // completas (antes AATI solo se cacheaba después de elegir un municipio,
  // así que si nunca se llegaba a elegir uno estando online, el catálogo
  // nunca quedaba guardado para usarlo luego sin conexión).
  useEffect(() => {
    if (!show) return;
    obtenerTerritorios().then(data => setTerritorios(data)).catch(() => setTerritorios([]));
    obtenerAatis().then(data => setAatisTodas(data)).catch(() => setAatisTodas([]));
    obtenerComunidades().then(data => setComunidadesTodas(data)).catch(() => setComunidadesTodas([]));
    obtenerEapbs().then(data => setEapbs(data)).catch(() => setEapbs([]));
    obtenerEtnias().then(data => setEtniasDb(data)).catch(() => setEtniasDb([]));
  }, [show]);

  // El filtro por municipio ahora es solo en JS sobre lo ya cargado — no
  // dispara ninguna llamada nueva a la red al cambiar el municipio.
  const aatis = form.municipio
    ? aatisTodas.filter(a => (a.territorio || "").trim().toLowerCase() === form.municipio.trim().toLowerCase())
    : [];
  const comunidades = form.municipio
    ? comunidadesTodas.filter(c => (c.territorio || "").trim().toLowerCase() === form.municipio.trim().toLowerCase())
    : [];

  const opcionesTerritorios = territorios.map(t => ({ value: t.nombre.trim(), label: t.nombre.trim() }));
  const opcionesAatis = aatis.map(a => ({ value: a.aati, label: `${a.aati} — ${a.nombre_completo}` }));
  const opcionesComunidades = comunidades.map(c => ({
    value: c.comunidad,
    label: c.microterritorio ? `${c.comunidad} (${c.microterritorio})` : c.comunidad,
  }));
  const opcionesEapbs = [
    { value: "NO ESTÁ AFILIADO", label: "NO ESTÁ AFILIADO" },
    ...eapbs.map(e => ({ value: e.nombre, label: e.nombre })),
  ];
  const opcionesEtnias = etniasDb.map(e => ({ value: e.nombre, label: e.nombre }));

  const estiloSelectInvalido = (invalido) => ({
    control: (base) => ({ ...base, borderColor: invalido ? "#dc3545" : base.borderColor }),
  });

  // ── Cargar persona al editar ────────────────────────────────────────────────
  useEffect(() => {
    if (!show) { resetFields(); return; }
    if (persona) {
      const fechaApl = parseFecha(persona.fecha);
      const fechaNac = parseFecha(persona.fecha_nacimiento);
      // Sequelize devuelve las asociaciones con nombres pluralizados
      // impredecibles (Acompanantes, Dificultads, ProductoApoyos...), no
      // "acompanantes"/"dificultades" en minúscula — por eso se prueban
      // varios nombres posibles (mismo criterio que excel.controller.js
      // y buildHtml.js en el backend).
      const getAsoc = (...posiblesNombres) => {
        for (const nombre of posiblesNombres) {
          if (Array.isArray(persona[nombre]) && persona[nombre].length > 0) return persona[nombre];
        }
        return [];
      };

      const acompanantesData    = getAsoc("Acompanantes", "acompanantes", "Acompanante");
      const entrevistadoresData = getAsoc("Entrevistadors", "Entrevistadores", "entrevistadores", "Entrevistador");

      setForm({
        ...initialForm,
        ...Object.fromEntries(
          Object.entries(persona).map(([k,v]) => [
            k,
            Array.isArray(initialForm[k])
              ? (Array.isArray(v) ? v : [])
              : typeof initialForm[k] === "boolean"
              ? (v ?? false) : (v ?? "")
          ])
        ),
        fecha_dia: fechaApl.dia, fecha_mes: fechaApl.mes, fecha_anio: fechaApl.anio,
        fecha_nac_dia: fechaNac.dia, fecha_nac_mes: fechaNac.mes, fecha_nac_anio: fechaNac.anio,
        acompanantes:    acompanantesData.length    ? acompanantesData    : initialForm.acompanantes,
        entrevistadores: entrevistadoresData.length ? entrevistadoresData : initialForm.entrevistadores,
        dificultades:              [{ descripcion: getAsoc("Dificultads","Dificultades","dificultades","Dificultad").map(d=>d.descripcion) }],
        productos_apoyos:          [{ descripcion: getAsoc("ProductoApoyos","productos_apoyos","ProductoApoyo").map(d=>d.descripcion) }],
        ayudas_tecnicas:           [{ descripcion: getAsoc("AyudaTecnicas","ayudas_tecnicas","AyudaTecnica").map(d=>d.descripcion) }],
        categorias_discapacidades: [{ descripcion: getAsoc("CategoriaDiscapacidads","CategoriaDiscapacidades","categorias_discapacidades","CategoriaDiscapacidad").map(d=>d.descripcion) }],
      });
      setStep(1); return;
    }
    resetFields();
  }, [show, persona]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Autocompletar gestor con el usuario logueado ────────────────────────────
  useEffect(() => {
    if (!show || persona) return;
    setForm(prev => ({ ...prev, gestor: user?.nombre || prev.gestor || "" }));
  }, [show, user, persona]);

  // ── Borrador local ───────────────────────────────────────────────────────────
  useEffect(() => { localStorage.setItem("rbc_disc_borrador", JSON.stringify(form)); }, [form]);
  useEffect(() => {
    if (!show || persona) return;
    const draft = localStorage.getItem("rbc_disc_borrador");
    if (!draft) return;
    const b = JSON.parse(draft);
    setForm(prev => ({ ...prev, ...b, gestor: user?.nombre || b.gestor || "" }));
  }, [show, user, persona]);

  // ── Calcular edad automáticamente al cambiar fecha de nacimiento ─────────────
  useEffect(() => {
    if (form.fecha_nac_dia && form.fecha_nac_mes && form.fecha_nac_anio) {
      const { anios, meses } = calcularEdad(form.fecha_nac_dia, form.fecha_nac_mes, form.fecha_nac_anio);
      setForm(prev => ({ ...prev, edad_anios: String(anios), edad_meses: String(meses) }));
    }
  }, [form.fecha_nac_dia, form.fecha_nac_mes, form.fecha_nac_anio]);

  // ── Calcular IMC automáticamente ──────────────────────────────────────────
  useEffect(() => {
    const peso  = parseFloat(form.peso);
    const talla = parseFloat(form.talla);
    if (peso > 0 && talla > 0) {
      const tallaMt = talla / 100;
      const imc     = (peso / (tallaMt * tallaMt)).toFixed(1);
      setForm(prev => ({ ...prev, imc }));
    }
  }, [form.peso, form.talla]);

  // Georreferenciación obligatoria: apenas se entra al paso 3 sin
  // coordenadas todavía, se dispara la solicitud de ubicación sola — así
  // el navegador muestra el cuadro de permiso sin que el usuario tenga que
  // acordarse de tocar el botón. Si ya hay coordenadas (ej. editando un
  // registro que ya las tenía), no se vuelve a pedir.
  useEffect(() => {
    if (show && step === 3 && !form.latitud && !form.longitud) {
      obtenerUbicacion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, step]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const resetFields = () => {
    setForm({ ...initialForm, gestor: user?.nombre || "" });
    setStep(1); setErrores({});
  };
  const f = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const req = txt => <>{txt} <span style={{ color:"red" }}>*</span></>;

  const edadAnios = parseInt(form.edad_anios) || 0;

  // Toma la ubicación GPS del dispositivo (funciona offline también — el
  // GPS del teléfono no necesita internet, solo el navegador necesita
  // permiso). No es obligatorio: si el dispositivo no tiene GPS o el
  // usuario no da permiso, el registro se puede guardar igual sin esto.
  const obtenerUbicacion = () => {
    setErrorGps("");
    if (!navigator.geolocation) {
      setErrorGps("Este dispositivo/navegador no soporta geolocalización.");
      return;
    }
    setObteniendoGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        f("latitud", pos.coords.latitude.toFixed(6));
        f("longitud", pos.coords.longitude.toFixed(6));
        f("precision_gps", Math.round(pos.coords.accuracy));
        setObteniendoGps(false);
      },
      (err) => {
        setObteniendoGps(false);
        const mensajes = {
          1: "Permiso de ubicación denegado. Actívalo en la configuración del navegador/app.",
          2: "No se pudo determinar la ubicación (sin señal GPS).",
          3: "Se agotó el tiempo esperando la ubicación. Intenta de nuevo en un lugar más abierto.",
        };
        setErrorGps(mensajes[err.code] || "No se pudo obtener la ubicación.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // ── CheckList con lógica de Ninguno exclusivo ─────────────────────────────────
  const CheckListNinguno = ({ campo, opciones, label }) => {
    const seleccionados = form[campo]?.[0]?.descripcion || [];
    const tieneNinguno  = seleccionados.includes("Ninguno");

    const toggle = (op, checked) => {
      let arr = [...seleccionados];
      if (op === "Ninguno") {
        arr = checked ? ["Ninguno"] : [];
      } else {
        if (tieneNinguno) return;
        arr = checked ? [...arr, op] : arr.filter(x => x !== op);
      }
      f(campo, [{ descripcion: arr }]);
    };

    return (
      <Form.Group className="mb-3">
        {label && <Form.Label className="d-block fw-semibold">{label}</Form.Label>}
        {opciones.map(op => (
          <Form.Check key={op} type="checkbox" label={op}
            checked={seleccionados.includes(op)}
            disabled={op !== "Ninguno" && tieneNinguno}
            onChange={e => toggle(op, e.target.checked)}
          />
        ))}
        {errores[campo] && <div className="text-danger mt-1" style={{ fontSize:"0.875em" }}>{errores[campo]}</div>}
      </Form.Group>
    );
  };

  const SiNo = ({ campo, label }) => (
    <Form.Group className="mb-3">
      {label && <Form.Label className="d-block">{req(label)}</Form.Label>}
      <div className="d-flex gap-3">
        {["Si","No"].map(op => (
          <Form.Check key={op} type="radio" inline label={op} value={op}
            checked={form[campo]===op} onChange={e => f(campo, e.target.value)} />
        ))}
      </div>
      {errores[campo] && <div className="text-danger" style={{ fontSize:"0.875em" }}>{errores[campo]}</div>}
    </Form.Group>
  );

  // ── Validación por paso ───────────────────────────────────────────────────────
  const validar = () => {
    const e = {};

    if (step === 1) {
      if (!form.municipio)  e.municipio = "Obligatorio";
      if (!form.fecha_dia || !form.fecha_mes || !form.fecha_anio) e.fecha = "Fecha completa obligatoria";
    }

    if (step === 2) {
      if (!form.tipo_documento)  e.tipo_documento  = "Obligatorio";
      if (!form.documento)       e.documento       = "Obligatorio";
      if (!form.primer_nombre)   e.primer_nombre   = "Obligatorio";
      if (!form.primer_apellido) e.primer_apellido = "Obligatorio";
      if (!form.sexo)            e.sexo            = "Obligatorio";
      if (!form.fecha_nac_dia || !form.fecha_nac_mes || !form.fecha_nac_anio) e.fecha_nac = "Fecha de nacimiento obligatoria";
      if (!form.afiliado_a)      e.afiliado_a      = "Obligatorio";
      if (!form.peso)                       e.peso                       = "Obligatorio";
      if (!form.talla)                      e.talla                      = "Obligatorio";
      if (!form.alimentos_complementarios)  e.alimentos_complementarios  = "Obligatorio";
      if (!form.utiliza_paniales)           e.utiliza_paniales           = "Obligatorio";
      if (!form.eps_suministra_paniales)    e.eps_suministra_paniales    = "Obligatorio";
    }

    if (step === 3) {
      if (!form.area)    e.area    = "Obligatorio";
      if (!form.nombre_comunidad) e.nombre_comunidad = "Obligatorio";
      if (!form.direccion) e.direccion = "Obligatorio";
      if (!form.latitud || !form.longitud) e.ubicacion = "Obligatorio — captura la ubicación GPS o escríbela a mano";
    }

    if (step === 5) {
      if (!form.etnia)             e.etnia             = "Obligatorio";
      if (form.etnia === "Indígena" && !form.cultura_indigena) e.cultura_indigena = "Obligatorio";
      if (!form.orientacion_sexual) e.orientacion_sexual = "Obligatorio";
    }

    if (step === 6) {
      if (!(form.dificultades[0]?.descripcion?.length))              e.dificultades              = "Seleccione al menos una opción";
      if (!(form.productos_apoyos[0]?.descripcion?.length))          e.productos_apoyos          = "Seleccione al menos una opción";
      if (!(form.ayudas_tecnicas[0]?.descripcion?.length))           e.ayudas_tecnicas           = "Seleccione al menos una opción";
      if (!(form.categorias_discapacidades[0]?.descripcion?.length)) e.categorias_discapacidades = "Seleccione al menos una categoría";
    }

    if (step === 7) {
      const campos7 = [
        "salud_promocion_eps","salud_programas_eps","salud_prevencion_conoce",
        "salud_seguridad_orientacion","salud_atencion_discapacidad","salud_valoracion_interdisciplinar",
        "salud_remision_pendiente","salud_rehabilitacion_eps","salud_orientacion_familia",
        "salud_dispositivo_ordenado","salud_entrenamiento_dispositivo","salud_entrega_dispositivo",
      ];
      campos7.forEach(c => { if (!form[c]) e[c] = "Obligatorio"; });
    }

    if (step === 8) {
      if (edadAnios >= 5) {
        if (!form.educacion_sabe_leer)            e.educacion_sabe_leer            = "Obligatorio";
        if (!form.educacion_asiste)               e.educacion_asiste               = "Obligatorio";
        if (!form.educacion_responde_necesidades) e.educacion_responde_necesidades = "Obligatorio";
        if (!form.educacion_ultimo_nivel)         e.educacion_ultimo_nivel         = "Obligatorio";
      }
      if (edadAnios >= 16) {
        if (!form.trabajo_actualmente)        e.trabajo_actualmente        = "Obligatorio";
        if (!form.trabajo_apoyo)              e.trabajo_apoyo              = "Obligatorio";
        if (!form.trabajo_capacidad_afectada) e.trabajo_capacidad_afectada = "Obligatorio";
      }
      if (edadAnios >= 10) {
        if (!form.participacion_actividades)      e.participacion_actividades      = "Obligatorio";
        if (!form.participacion_decisiones)       e.participacion_decisiones       = "Obligatorio";
        if (!form.participacion_conoce_derechos)  e.participacion_conoce_derechos  = "Obligatorio";
      }
    }

    if (step === 9) {
      if (!form.entorno_barreras_fisicas)    e.entorno_barreras_fisicas    = "Obligatorio";
      if (!form.entorno_actitudes_negativas) e.entorno_actitudes_negativas = "Obligatorio";
      if (!form.cuidado_requiere_ayuda)      e.cuidado_requiere_ayuda      = "Obligatorio";
      if (!form.cuidado_barreras_vivienda)   e.cuidado_barreras_vivienda   = "Obligatorio";
    }

    if (step === 10) {
      if (!form.rlcpcd_primera_vez && !form.rlcpcd_actualizacion && !form.rlcpcd_certificado)
        e.rlcpcd = "Seleccione al menos una opción";
      if (!form.entrevistadores[0]?.nombres_apellidos) e.entrevistador_nombre = "Obligatorio";
      if (!form.entrevistadores[0]?.profesion_cargo)   e.entrevistador_cargo  = "Obligatorio";
      if (!form.entrevistadores[0]?.numero_documento)  e.entrevistador_doc    = "Obligatorio";
    }

    return e;
  };

  const nextStep = () => {
    const e = validar();
    if (Object.keys(e).length) { setErrores(e); return; }
    setErrores({});
    if (step < TOTAL_PASOS) setStep(step + 1);
  };
  const prevStep = () => { if (step > 1) setStep(step - 1); };

  const handleClose = async () => {
    const result = await Swal.fire({ title:"¿Salir del formulario?", text:"El progreso no guardado se perderá.", icon:"warning", showCancelButton:true, confirmButtonColor:"#d33", cancelButtonColor:"#3085d6", confirmButtonText:"Sí, salir", cancelButtonText:"Cancelar" });
    if (result.isConfirmed) onClose();
  };

  // ── Guardar ──────────────────────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validar();
    if (Object.keys(errs).length) { setErrores(errs); return; }
    try {
      setLoading(true);
      const payload = {
        ...form,
        fecha: form.fecha_anio && form.fecha_mes && form.fecha_dia
          ? `${form.fecha_anio}-${String(form.fecha_mes).padStart(2,"0")}-${String(form.fecha_dia).padStart(2,"0")}` : "",
        fecha_nacimiento: form.fecha_nac_anio && form.fecha_nac_mes && form.fecha_nac_dia
          ? `${form.fecha_nac_anio}-${String(form.fecha_nac_mes).padStart(2,"0")}-${String(form.fecha_nac_dia).padStart(2,"0")}` : "",
        gestor: user?.nombre || form.gestor,
        dificultades_diarias:    (form.dificultades[0]?.descripcion              || []),
        productos_apoyo:         (form.productos_apoyos[0]?.descripcion          || []),
        ayudas_tecnicas:         (form.ayudas_tecnicas[0]?.descripcion           || []),
        categorias_discapacidad: (form.categorias_discapacidades[0]?.descripcion || []),
      };
      const estaOnline = online && navigator.onLine;
      if (estaOnline) {
        if (persona) {
          await updatePersona(persona.id, payload);
          await Swal.fire({ icon:"success", title:"✏️ Registro actualizado", timer:1500, showConfirmButton:false });
        } else {
          await createPersona(payload);
          await Swal.fire({ icon:"success", title:"✅ Persona registrada", timer:1500, showConfirmButton:false });
        }
      } else {
        await guardarOfflineDiscapacidad(payload, persona?.id);
        await Swal.fire({ icon:"info", title:"📴 Sin conexión", text:"El registro se guardó localmente y se sincronizará al reconectarse.", confirmButtonColor:"#6f42c1" });
      }
      localStorage.removeItem("rbc_disc_borrador");
      onClose();
      if (reload) await reload();
    } catch (error) {
      if (!manejarErrorDuplicado(error,"Discapacidad")) {
        Swal.fire({ icon:"error", title:"Error al guardar", text: error.response?.data?.message || error.message || "Error desconocido", confirmButtonColor:"#d33" });
      }
    } finally { setLoading(false); }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Formato RBC – Identificación y Evaluación Persona con Discapacidad
          {!online && <span className="badge bg-warning text-dark ms-2" style={{fontSize:"0.7rem"}}>📴 Offline</span>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-4">
          <div style={{ height:8, background:"#ddd", borderRadius:20 }}>
            <div style={{ width:`${(step/TOTAL_PASOS)*100}%`, height:8, background:"#0d6efd", borderRadius:20, transition:"width 0.3s" }} />
          </div>
          <p className="mt-2 text-muted small">Paso {step} de {TOTAL_PASOS}</p>
        </div>

        <Form onSubmit={handleSubmit}>

          {/* ═══ PASO 1 ═════════════════════════════════════════════════════ */}
          {step === 1 && (
            <>
              <h5 className="mb-3">1. Lugar y fecha de la aplicación</h5>
              <Form.Group className="mb-3">
                <Form.Label>Entidad / Institución</Form.Label>
                <Form.Control value={form.entidad} readOnly plaintext style={{ background:"#f8f9fa", fontWeight:500 }} />
              </Form.Group>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <Form.Label>Departamento</Form.Label>
                  <Form.Control value={form.departamento} readOnly plaintext style={{ background:"#f8f9fa", fontWeight:500 }} />
                </div>
                <div className="col-md-4">
                  <Form.Label>{req("Municipio / Corregimiento")}</Form.Label>
                  <Select
                    classNamePrefix="rs"
                    styles={estiloSelectInvalido(!!errores.municipio)}
                    placeholder="Buscar municipio..."
                    noOptionsMessage={() => "Sin resultados"}
                    options={opcionesTerritorios}
                    value={opcionesTerritorios.find(o => o.value === form.municipio) || null}
                    onChange={opt => {
                      f("municipio", opt?.value || "");
                      f("asociacion_indigena", ""); // depende del municipio — se limpia al cambiar
                      f("nombre_comunidad", "");     // ídem — la comunidad también depende del municipio
                    }}
                  />
                  {errores.municipio && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores.municipio}</div>}
                </div>
                <div className="col-md-4">
                  <Form.Label>Asociación Indígena (AATI)</Form.Label>
                  <Select
                    classNamePrefix="rs"
                    placeholder={form.municipio ? "Buscar AATI (o dejar vacío si no aplica)..." : "Selecciona primero el municipio"}
                    noOptionsMessage={() => form.municipio ? "Sin AATI para este municipio" : "Selecciona primero el municipio"}
                    isDisabled={!form.municipio}
                    isClearable
                    options={opcionesAatis}
                    value={opcionesAatis.find(o => o.value === form.asociacion_indigena) || null}
                    onChange={opt => f("asociacion_indigena", opt?.value || "")}
                  />
                </div>
              </div>
              <Form.Label>{req("Fecha de aplicación")}</Form.Label>
              {errores.fecha && <div className="text-danger mb-2" style={{fontSize:"0.875em"}}>{errores.fecha}</div>}
              <div className="d-flex gap-2 mb-3">
                <Form.Control placeholder="Día"  type="number" min="1"  max="31"  value={form.fecha_dia}  onChange={e=>f("fecha_dia",e.target.value)}  style={{width:80}} />
                <Form.Control placeholder="Mes"  type="number" min="1"  max="12"  value={form.fecha_mes}  onChange={e=>f("fecha_mes",e.target.value)}  style={{width:80}} />
                <Form.Control placeholder="Año"  type="number"                    value={form.fecha_anio} onChange={e=>f("fecha_anio",e.target.value)} style={{width:110}} />
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Gestor / Técnico</Form.Label>
                <Form.Control value={form.gestor} readOnly plaintext style={{background:"#f8f9fa",fontWeight:500}} />
              </Form.Group>
            </>
          )}

          {/* ═══ PASO 2 ═════════════════════════════════════════════════════ */}
          {step === 2 && (
            <>
              <h5 className="mb-3">2. Datos personales de la persona con discapacidad</h5>
              <Form.Group className="mb-3">
                <Form.Label>{req("Tipo de identificación")}</Form.Label>
                <Form.Select value={form.tipo_documento} isInvalid={!!errores.tipo_documento} onChange={e=>f("tipo_documento",e.target.value)}>
                  <option value="">Seleccione</option>
                  {TIPOS_DOC_PERSONA.map(t=><option key={t}>{t}</option>)}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errores.tipo_documento}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{req("Número de documento")}</Form.Label>
                <Form.Control value={form.documento} isInvalid={!!errores.documento} onChange={e=>f("documento",e.target.value)} />
                <Form.Control.Feedback type="invalid">{errores.documento}</Form.Control.Feedback>
              </Form.Group>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <Form.Label>{req("Primer nombre")}</Form.Label>
                  <Form.Control value={form.primer_nombre} isInvalid={!!errores.primer_nombre} onChange={e=>f("primer_nombre",e.target.value)} />
                  <Form.Control.Feedback type="invalid">{errores.primer_nombre}</Form.Control.Feedback>
                </div>
                <div className="col-md-6">
                  <Form.Label>Segundo nombre</Form.Label>
                  <Form.Control value={form.segundo_nombre} onChange={e=>f("segundo_nombre",e.target.value)} />
                </div>
                <div className="col-md-6">
                  <Form.Label>{req("Primer apellido")}</Form.Label>
                  <Form.Control value={form.primer_apellido} isInvalid={!!errores.primer_apellido} onChange={e=>f("primer_apellido",e.target.value)} />
                  <Form.Control.Feedback type="invalid">{errores.primer_apellido}</Form.Control.Feedback>
                </div>
                <div className="col-md-6">
                  <Form.Label>Segundo apellido</Form.Label>
                  <Form.Control value={form.segundo_apellido} onChange={e=>f("segundo_apellido",e.target.value)} />
                </div>
              </div>

              <Form.Label>{req("Fecha de nacimiento")}</Form.Label>
              {errores.fecha_nac && <div className="text-danger mb-2" style={{fontSize:"0.875em"}}>{errores.fecha_nac}</div>}
              <div className="d-flex gap-2 mb-1">
                <Form.Control placeholder="Día" type="number" min="1" max="31" value={form.fecha_nac_dia}  onChange={e=>f("fecha_nac_dia",e.target.value)}  style={{width:80}} />
                <Form.Control placeholder="Mes" type="number" min="1" max="12" value={form.fecha_nac_mes}  onChange={e=>f("fecha_nac_mes",e.target.value)}  style={{width:80}} />
                <Form.Control placeholder="Año" type="number"                  value={form.fecha_nac_anio} onChange={e=>f("fecha_nac_anio",e.target.value)} style={{width:110}} />
              </div>
              {form.fecha_nac_anio && (
                <div style={{fontSize:12,color:"#059669",marginBottom:12}}>
                  ✅ Edad calculada: <strong>{form.edad_anios} años, {form.edad_meses} meses</strong>
                </div>
              )}

              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <Form.Label>{req("Edad (años)")}</Form.Label>
                  <Form.Control type="number" min="0" value={form.edad_anios} onChange={e=>f("edad_anios",e.target.value)} />
                </div>
                <div className="col-md-4">
                  <Form.Label>{req("Edad (meses)")}</Form.Label>
                  <Form.Control type="number" min="0" max="11" value={form.edad_meses} onChange={e=>f("edad_meses",e.target.value)} />
                </div>
                <div className="col-md-4">
                  <Form.Label>{req("Sexo")}</Form.Label>
                  <Form.Select value={form.sexo} isInvalid={!!errores.sexo} onChange={e=>f("sexo",e.target.value)}>
                    <option value="">Seleccione</option>
                    <option>Masculino</option>
                    <option>Femenino</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errores.sexo}</Form.Control.Feedback>
                </div>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Diagnóstico médico (Dx)</Form.Label>
                <Form.Control value={form.diagnostico} isInvalid={!!errores.diagnostico} onChange={e=>f("diagnostico",e.target.value)} />
                <Form.Control.Feedback type="invalid">{errores.diagnostico}</Form.Control.Feedback>
              </Form.Group>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <Form.Label>{req("Afiliado a (EPS / EAPB)")}</Form.Label>
                  <Select
                    classNamePrefix="rs"
                    styles={estiloSelectInvalido(!!errores.afiliado_a)}
                    placeholder="Buscar EPS/EAPB..."
                    noOptionsMessage={() => "Sin resultados"}
                    options={opcionesEapbs}
                    value={opcionesEapbs.find(o => o.value === form.afiliado_a) || null}
                    onChange={opt => { f("afiliado_a", opt?.value || ""); f("eps", opt?.value || ""); }}
                  />
                  {errores.afiliado_a && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores.afiliado_a}</div>}
                </div>
                <div className="col-md-3">
                  <Form.Label>Sisbén No.</Form.Label>
                  <Form.Control value={form.sisben_no} isInvalid={!!errores.sisben_no} onChange={e=>f("sisben_no",e.target.value)} />
                  <Form.Control.Feedback type="invalid">{errores.sisben_no}</Form.Control.Feedback>
                </div>
                <div className="col-md-3">
                  <Form.Label>Nivel Sisbén</Form.Label>
                  <Form.Control value={form.sisben_nivel} isInvalid={!!errores.sisben_nivel} onChange={e=>f("sisben_nivel",e.target.value)} />
                  <Form.Control.Feedback type="invalid">{errores.sisben_nivel}</Form.Control.Feedback>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <Form.Label>{req("Peso (kg)")}</Form.Label>
                  <Form.Control type="number" value={form.peso} isInvalid={!!errores.peso} onChange={e=>f("peso",e.target.value)} />
                  <Form.Control.Feedback type="invalid">{errores.peso}</Form.Control.Feedback>
                </div>
                <div className="col-md-3">
                  <Form.Label>{req("Talla (cm)")}</Form.Label>
                  <Form.Control type="number" value={form.talla} isInvalid={!!errores.talla} onChange={e=>f("talla",e.target.value)} />
                  <Form.Control.Feedback type="invalid">{errores.talla}</Form.Control.Feedback>
                </div>
                <div className="col-md-3">
                  <Form.Label>IMC (calculado)</Form.Label>
                  <Form.Control value={form.imc} readOnly
                    style={{ background: form.imc ? "#f0fdf4" : "#f8f9fa", fontWeight: form.imc ? 700 : 400, color: form.imc ? "#065f46" : "#6c757d" }} />
                  {form.imc && (
                    <div style={{ fontSize: 11, color: "#059669", marginTop: 3 }}>
                      ✅ {parseFloat(form.imc) < 18.5 ? "Bajo peso" : parseFloat(form.imc) < 25 ? "Normal" : parseFloat(form.imc) < 30 ? "Sobrepeso" : "Obesidad"}
                    </div>
                  )}
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <Form.Label>{req("Consume alimentos complementarios")}</Form.Label>
                  <div className="d-flex gap-3">{["Si","No"].map(op=><Form.Check key={op} type="radio" inline label={op} value={op} checked={form.alimentos_complementarios===op} onChange={e=>f("alimentos_complementarios",e.target.value)} />)}</div>
                  {errores.alimentos_complementarios && <div className="text-danger" style={{fontSize:"0.875em"}}>{errores.alimentos_complementarios}</div>}
                </div>
                <div className="col-md-4">
                  <Form.Label>{req("Utiliza pañales")}</Form.Label>
                  <div className="d-flex gap-3">{["Si","No"].map(op=><Form.Check key={op} type="radio" inline label={op} value={op} checked={form.utiliza_paniales===op} onChange={e=>f("utiliza_paniales",e.target.value)} />)}</div>
                  {errores.utiliza_paniales && <div className="text-danger" style={{fontSize:"0.875em"}}>{errores.utiliza_paniales}</div>}
                </div>
                <div className="col-md-4">
                  <Form.Label>{req("La EPS los suministra")}</Form.Label>
                  <div className="d-flex gap-3">{["Si","No"].map(op=><Form.Check key={op} type="radio" inline label={op} value={op} checked={form.eps_suministra_paniales===op} onChange={e=>f("eps_suministra_paniales",e.target.value)} />)}</div>
                  {errores.eps_suministra_paniales && <div className="text-danger" style={{fontSize:"0.875em"}}>{errores.eps_suministra_paniales}</div>}
                </div>
              </div>
            </>
          )}

          {/* ═══ PASO 3 ═════════════════════════════════════════════════════ */}
          {step === 3 && (
            <>
              <h5 className="mb-3">3. Datos de residencia</h5>
              <div className="row g-3 mb-3">
                <div className="col-md-8">
                  <Form.Label>{req("Direccion")}</Form.Label>
                  <Form.Control value={form.direccion} onChange={e=>f("direccion",e.target.value)} />
                </div>
                <div className="col-md-4">
                  <Form.Label>Barrio</Form.Label>
                  <Form.Control value={form.barrio} onChange={e=>f("barrio",e.target.value)} />
                </div>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>{req("Área")}</Form.Label>
                <div className="d-flex gap-4">
                  {["Casco urbano","Rural disperso","Cabecera municipal"].map(op=>(
                    <Form.Check key={op} type="radio" inline label={op} value={op} checked={form.area===op} onChange={e=>f("area",e.target.value)} />
                  ))}
                </div>
                {errores.area && <div className="text-danger" style={{fontSize:"0.875em"}}>{errores.area}</div>}
              </Form.Group>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <Form.Label>{req("Nombre comunidad")}</Form.Label>
                  <Select
                    classNamePrefix="rs"
                    styles={estiloSelectInvalido(!!errores.nombre_comunidad)}
                    placeholder={form.municipio ? "Buscar comunidad..." : "Selecciona primero el municipio (paso 1)"}
                    noOptionsMessage={() => form.municipio ? "Sin comunidades para este municipio" : "Selecciona primero el municipio"}
                    isDisabled={!form.municipio}
                    options={opcionesComunidades}
                    value={opcionesComunidades.find(o => o.value === form.nombre_comunidad) || null}
                    onChange={opt => f("nombre_comunidad", opt?.value || "")}
                  />
                  {errores.nombre_comunidad && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores.nombre_comunidad}</div>}
                </div>
                <div className="col-md-6">
                  <Form.Label>Corregimiento</Form.Label>
                  <Form.Control value={form.corregimiento} onChange={e=>f("corregimiento",e.target.value)} />
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Label>Teléfono / Celular</Form.Label>
                  <Form.Control value={form.telefono} onChange={e=>f("telefono",e.target.value)} />
                </div>
                <div className="col-md-6">
                  <Form.Label>Correo electrónico</Form.Label>
                  <Form.Control type="email" value={form.correo} onChange={e=>f("correo",e.target.value)} />
                </div>
              </div>

              <div className="border rounded p-3 mt-2 bg-light" style={errores.ubicacion ? { borderColor: "#dc3545" } : {}}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">{req("📍 Georreferenciación")}</Form.Label>
                  <Button size="sm" variant="outline-primary" onClick={obtenerUbicacion} disabled={obteniendoGps}>
                    {obteniendoGps ? <><span className="spinner-border spinner-border-sm me-1" />Ubicando...</> : "📍 Obtener ubicación actual"}
                  </Button>
                </div>
                {errorGps && <div className="text-danger mb-2" style={{ fontSize: "0.85em" }}>{errorGps}</div>}
                {errores.ubicacion && <div className="text-danger mb-2" style={{ fontSize: "0.85em" }}>{errores.ubicacion}</div>}
                <div className="row g-3">
                  <div className="col-md-4">
                    <Form.Label style={{ fontSize: "0.8rem" }}>Latitud</Form.Label>
                    <Form.Control value={form.latitud} isInvalid={!!errores.ubicacion} placeholder="Ej. -4.203889" onChange={e=>f("latitud",e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <Form.Label style={{ fontSize: "0.8rem" }}>Longitud</Form.Label>
                    <Form.Control value={form.longitud} isInvalid={!!errores.ubicacion} placeholder="Ej. -69.940556" onChange={e=>f("longitud",e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <Form.Label style={{ fontSize: "0.8rem" }}>Precisión (m)</Form.Label>
                    <Form.Control value={form.precision_gps} readOnly plaintext style={{ background: "#fff", fontSize: "0.85rem" }} />
                  </div>
                </div>
                <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                  Funciona sin internet (usa el GPS del dispositivo) — el navegador va a pedir permiso de ubicación apenas llegues a este paso. Si el permiso queda denegado, puedes escribir las coordenadas a mano.
                </div>
              </div>
            </>
          )}

          {/* ═══ PASO 4 ═════════════════════════════════════════════════════ */}
          {step === 4 && (
            <>
              <h5 className="mb-3">4. Datos de la persona que acompaña la valoración <small className="text-muted fs-6">(si aplica)</small></h5>
              {form.acompanantes.map((ac,i)=>(
                <div key={i} className="border rounded p-3 mb-3 bg-light">
                  <p className="mb-2 text-muted small">Acompañante {i+1}</p>
                  <div className="row g-3 mb-3">
                    {["primer_nombre","segundo_nombre","primer_apellido","segundo_apellido"].map(field=>(
                      <div key={field} className="col-md-6">
                        <Form.Label>{field.replace("_"," ").replace(/\b\w/g,l=>l.toUpperCase())}</Form.Label>
                        <Form.Control value={ac[field]||""} onChange={e=>{const arr=[...form.acompanantes];arr[i]={...arr[i],[field]:e.target.value};f("acompanantes",arr);}} />
                      </div>
                    ))}
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Label>Tipo de documento</Form.Label>
                      <Form.Select value={ac.tipo_doc||""} onChange={e=>{const arr=[...form.acompanantes];arr[i]={...arr[i],tipo_doc:e.target.value};f("acompanantes",arr);}}>
                        <option value="">Seleccione</option>
                        {TIPOS_DOC_ACOMPANANTE.map(t=><option key={t}>{t}</option>)}
                      </Form.Select>
                    </div>
                    <div className="col-md-6">
                      <Form.Label>Número de documento</Form.Label>
                      <Form.Control value={ac.numero_doc||""} onChange={e=>{const arr=[...form.acompanantes];arr[i]={...arr[i],numero_doc:e.target.value};f("acompanantes",arr);}} />
                    </div>
                    <div className="col-md-6">
                      <Form.Label>Parentesco</Form.Label>
                      <Form.Select value={ac.parentesco||""} onChange={e=>{const arr=[...form.acompanantes];arr[i]={...arr[i],parentesco:e.target.value};f("acompanantes",arr);}}>
                        <option value="">Seleccione</option>
                        {PARENTESCOS.map(p=><option key={p}>{p}</option>)}
                      </Form.Select>
                    </div>
                    <div className="col-md-6">
                      <Form.Label>Calidad del acompañante</Form.Label>
                      <div className="d-flex gap-3 mt-1">
                        {["Representante legal","Persona de apoyo"].map(op=>(
                          <Form.Check key={op} type="radio" inline label={op} value={op} checked={ac.calidad===op}
                            onChange={e=>{const arr=[...form.acompanantes];arr[i]={...arr[i],calidad:e.target.value};f("acompanantes",arr);}} />
                        ))}
                      </div>
                    </div>
                  </div>
                  {form.acompanantes.length > 1 && (
                    <div className="text-end mt-2">
                      <Button variant="outline-danger" size="sm" onClick={()=>f("acompanantes",form.acompanantes.filter((_,idx)=>idx!==i))}>🗑 Quitar</Button>
                    </div>
                  )}
                </div>
              ))}
              <Button variant="outline-primary" size="sm" onClick={()=>f("acompanantes",[...form.acompanantes,{primer_nombre:"",segundo_nombre:"",primer_apellido:"",segundo_apellido:"",tipo_doc:"",numero_doc:"",parentesco:"",calidad:""}])}>➕ Agregar acompañante</Button>
            </>
          )}

          {/* ═══ PASO 5 ═════════════════════════════════════════════════════ */}
          {step === 5 && (
            <>
              <h5 className="mb-3">5. Autoreconocimiento</h5>
              <Form.Group className="mb-3">
                <Form.Label>{req("5.1 Etnia con la que se identifica")}</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {ETNIAS.map(op=><Form.Check key={op} type="radio" inline label={op} value={op}
                    checked={form.etnia===op}
                    onChange={e=>{
                      f("etnia", e.target.value);
                      // 5.2 solo aplica si es Indígena — se limpia al cambiar a otra opción.
                      if (e.target.value !== "Indígena") { f("cultura_indigena",""); f("cultura_indigena_otro",""); }
                    }} />)}
                </div>
                {errores.etnia && <div className="text-danger" style={{fontSize:"0.875em"}}>{errores.etnia}</div>}
              </Form.Group>
              {form.etnia === "Indígena" && (
                <Form.Group className="mb-3">
                  <Form.Label>{req("5.2 Cultura/pueblo indígena con el que se identifica")}</Form.Label>
                  <Select
                    classNamePrefix="rs"
                    styles={estiloSelectInvalido(!!errores.cultura_indigena)}
                    placeholder="Buscar pueblo indígena..."
                    noOptionsMessage={() => "Sin resultados"}
                    options={opcionesEtnias}
                    value={opcionesEtnias.find(o => o.value === form.cultura_indigena) || null}
                    onChange={opt => f("cultura_indigena", opt?.value || "")}
                  />
                  {form.cultura_indigena === "Otra" && (
                    <Form.Control className="mt-2" placeholder="¿Cuál?" value={form.cultura_indigena_otro} onChange={e=>f("cultura_indigena_otro",e.target.value)} />
                  )}
                  {errores.cultura_indigena && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores.cultura_indigena}</div>}
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>{req("5.3 Orientación sexual")}</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {ORIENTACIONES.map(op=><Form.Check key={op} type="radio" inline label={op} value={op} checked={form.orientacion_sexual===op} onChange={e=>f("orientacion_sexual",e.target.value)} />)}
                </div>
                {errores.orientacion_sexual && <div className="text-danger" style={{fontSize:"0.875em"}}>{errores.orientacion_sexual}</div>}
              </Form.Group>
            </>
          )}

          {/* ═══ PASO 6 ═════════════════════════════════════════════════════ */}
          {step === 6 && (
            <>
              <h5 className="mb-3">6. Aspectos generales de la discapacidad</h5>
              <CheckListNinguno campo="dificultades"     opciones={DIFICULTADES_DIARIAS} label={req("6.1 Presenta dificultad en actividades diarias como:")} />
              <CheckListNinguno campo="productos_apoyos" opciones={PRODUCTOS_APOYO}      label={req("6.2 Productos de apoyo que actualmente utiliza:")} />
              <CheckListNinguno campo="ayudas_tecnicas"  opciones={AYUDAS_TECNICAS}      label={req("6.3 Requiere ayuda técnica:")} />
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">{req("6.4 Categoría de la discapacidad")}</Form.Label>
                {CATEGORIAS_DISCAPACIDAD.map(op=>(
                  <Form.Check key={op} type="checkbox" label={op}
                    checked={(form.categorias_discapacidades[0]?.descripcion||[]).includes(op)}
                    onChange={e=>{const arr=form.categorias_discapacidades[0]?.descripcion||[];f("categorias_discapacidades",[{descripcion:e.target.checked?[...arr,op]:arr.filter(x=>x!==op)}]);}} />
                ))}
                {errores.categorias_discapacidades && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores.categorias_discapacidades}</div>}
              </Form.Group>
            </>
          )}

          {/* ═══ PASO 7 ═════════════════════════════════════════════════════ */}
          {step === 7 && (
            <>
              <h5 className="mb-3">7. Servicios de salud</h5>
              <p className="fw-semibold text-primary mb-2">7.1 Promoción</p>
              <SiNo campo="salud_promocion_eps"               label="¿La EPS ha realizado actividades de promoción en salud?" />
              <SiNo campo="salud_programas_eps"               label="¿Sabe si su EPS maneja programas donde usted pueda estar incluido?" />
              <p className="fw-semibold text-primary mb-2">7.2 Prevención</p>
              <SiNo campo="salud_prevencion_conoce"           label="¿Conoce los servicios de prevención de su EPS-IPS?" />
              <SiNo campo="salud_seguridad_orientacion"       label="¿Le han orientado sobre seguridad en el trabajo/hogar?" />
              <p className="fw-semibold text-primary mb-2">7.3 Atención médica</p>
              <SiNo campo="salud_atencion_discapacidad"       label="¿Ha recibido atención en salud por su discapacidad?" />
              <SiNo campo="salud_valoracion_interdisciplinar" label="¿Ha recibido valoración por un equipo interdisciplinar?" />
              <SiNo campo="salud_remision_pendiente"          label="¿Tiene pendiente alguna remisión / autorización?" />
              <p className="fw-semibold text-primary mb-2">7.4 Rehabilitación</p>
              <SiNo campo="salud_rehabilitacion_eps"          label="¿Actualmente asiste a algún servicio de rehabilitación por su EPS?" />
              <SiNo campo="salud_orientacion_familia"         label="¿Usted o su familia ha recibido orientación sobre el manejo de la discapacidad?" />
              <p className="fw-semibold text-primary mb-2">7.5 Dispositivos de asistencia</p>
              <SiNo campo="salud_dispositivo_ordenado"        label="¿Le ordenaron algún dispositivo de asistencia?" />
              <SiNo campo="salud_entrenamiento_dispositivo"   label="¿Recibió entrenamiento para el manejo del dispositivo?" />
              <SiNo campo="salud_entrega_dispositivo"         label="¿La EPS le garantizó la entrega del dispositivo?" />
            </>
          )}

          {/* ═══ PASO 8 ═════════════════════════════════════════════════════ */}
          {step === 8 && (
            <>
              <h5 className="mb-3">8. Ejercicio de derechos</h5>

              {edadAnios >= 5 ? (
                <>
                  <p className="fw-semibold text-primary mb-2">8.1 Educación (mayores de 5 años)</p>
                  <SiNo campo="educacion_sabe_leer"            label="¿Sabe leer y escribir?" />
                  <SiNo campo="educacion_asiste"               label="¿Asiste actualmente a alguna institución educativa?" />
                  <SiNo campo="educacion_responde_necesidades" label="¿La educación recibida responde a sus necesidades?" />
                  <Form.Group className="mb-3">
                    <Form.Label>{req("8.2 Último nivel educativo completado")}</Form.Label>
                    <div className="d-flex gap-4">
                      {["Primaria","Secundaria","Ninguno"].map(op=>(
                        <Form.Check key={op} type="radio" inline label={op} value={op} checked={form.educacion_ultimo_nivel===op} onChange={e=>f("educacion_ultimo_nivel",e.target.value)} />
                      ))}
                    </div>
                    {errores.educacion_ultimo_nivel && <div className="text-danger" style={{fontSize:"0.875em"}}>{errores.educacion_ultimo_nivel}</div>}
                  </Form.Group>
                </>
              ) : (
                <div className="alert alert-info py-2 px-3 mb-3" style={{fontSize:13}}>
                  ℹ️ Sección educación no aplica — menor de 5 años ({edadAnios} años)
                </div>
              )}

              {edadAnios >= 16 ? (
                <>
                  <p className="fw-semibold text-primary mb-2">8.3 Trabajo (mayores de 16 años)</p>
                  <SiNo campo="trabajo_actualmente"        label="¿Actualmente está trabajando?" />
                  <SiNo campo="trabajo_apoyo"              label="¿Recibe apoyo u orientación en su proceso laboral?" />
                  <SiNo campo="trabajo_capacidad_afectada" label="¿Su capacidad en el trabajo ha sido afectada por su discapacidad?" />
                </>
              ) : (
                <div className="alert alert-info py-2 px-3 mb-3" style={{fontSize:13}}>
                  ℹ️ Sección trabajo no aplica — menor de 16 años ({edadAnios} años)
                </div>
              )}

              {edadAnios >= 10 ? (
                <>
                  <p className="fw-semibold text-primary mb-2">8.4 Participación (mayores de 10 años)</p>
                  <SiNo campo="participacion_actividades"     label="¿Participa en actividades familiares, comunitarias, religiosas, deportivas, etc.?" />
                  <SiNo campo="participacion_decisiones"      label="¿Toma decisiones de manera autónoma?" />
                  <SiNo campo="participacion_conoce_derechos" label="¿Usted y su familia conocen sus deberes y derechos como persona con discapacidad?" />
                </>
              ) : (
                <div className="alert alert-info py-2 px-3 mb-3" style={{fontSize:13}}>
                  ℹ️ Sección participación no aplica — menor de 10 años ({edadAnios} años)
                </div>
              )}
            </>
          )}

          {/* ═══ PASO 9 ═════════════════════════════════════════════════════ */}
          {step === 9 && (
            <>
              <h5 className="mb-3">9. Entorno para la vida y cuidado</h5>
              <p className="fw-semibold text-primary mb-2">9.1 Actividades de la vida diaria</p>
              <SiNo campo="entorno_barreras_fisicas"    label="¿Encuentra barreras físicas en vivienda, espacios públicos, transporte, edificios, etc.?" />
              <SiNo campo="entorno_actitudes_negativas" label="¿Ha recibido actitudes negativas de familia, amigos, comunidad, vecinos, etc.?" />
              <Form.Group className="mb-3">
                <Form.Label>Plan casero (Recomendaciones)</Form.Label>
                <Form.Control as="textarea" rows={3} value={form.entorno_plan_casero} onChange={e=>f("entorno_plan_casero",e.target.value)} />
              </Form.Group>
              <p className="fw-semibold text-primary mb-2">9.2 Cuidado</p>
              <SiNo campo="cuidado_requiere_ayuda"      label="¿Requiere permanentemente la ayuda de otra persona para actividades de la vida diaria?" />
              <SiNo campo="cuidado_barreras_vivienda"   label="¿En su vivienda o entorno físico encuentra barreras que le impidan desarrollar sus actividades con mayor autonomía?" />
            </>
          )}

          {/* ═══ PASO 10 ════════════════════════════════════════════════════ */}
          {step === 10 && (
            <>
              <h5 className="mb-3">10. Registro de Localización y Caracterización (RLCPCD)</h5>
              <Form.Group className="mb-3">
                <Form.Label>{req("Tipo de registro")}</Form.Label>
                <div className="d-flex gap-4">
                  <Form.Check type="checkbox" label="Por primera vez"             checked={form.rlcpcd_primera_vez}   onChange={e=>f("rlcpcd_primera_vez",  e.target.checked)} />
                  <Form.Check type="checkbox" label="Actualización de datos"      checked={form.rlcpcd_actualizacion} onChange={e=>f("rlcpcd_actualizacion",e.target.checked)} />
                  <Form.Check type="checkbox" label="Certificado de discapacidad" checked={form.rlcpcd_certificado}   onChange={e=>f("rlcpcd_certificado",  e.target.checked)} />
                </div>
                {errores.rlcpcd && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores.rlcpcd}</div>}
              </Form.Group>

              <p className="fw-semibold mb-2">Entrevista aplicada por — Profesional 1 {req("")}</p>
              <div className="border rounded p-3 mb-3 bg-light">
                <div className="row g-2">
                  <div className="col-md-5">
                    <Form.Label>{req("Nombres y apellidos")}</Form.Label>
                    <Form.Control value={form.entrevistadores[0]?.nombres_apellidos||""} isInvalid={!!errores.entrevistador_nombre}
                      onChange={e=>{const arr=[...form.entrevistadores];arr[0]={...arr[0],nombres_apellidos:e.target.value};f("entrevistadores",arr);}} />
                    <Form.Control.Feedback type="invalid">{errores.entrevistador_nombre}</Form.Control.Feedback>
                  </div>
                  <div className="col-md-4">
                    <Form.Label>{req("Profesión / Cargo")}</Form.Label>
                    <Form.Control value={form.entrevistadores[0]?.profesion_cargo||""} isInvalid={!!errores.entrevistador_cargo}
                      onChange={e=>{const arr=[...form.entrevistadores];arr[0]={...arr[0],profesion_cargo:e.target.value};f("entrevistadores",arr);}} />
                    <Form.Control.Feedback type="invalid">{errores.entrevistador_cargo}</Form.Control.Feedback>
                  </div>
                  <div className="col-md-3">
                    <Form.Label>{req("N° documento")}</Form.Label>
                    <Form.Control value={form.entrevistadores[0]?.numero_documento||""} isInvalid={!!errores.entrevistador_doc}
                      onChange={e=>{const arr=[...form.entrevistadores];arr[0]={...arr[0],numero_documento:e.target.value};f("entrevistadores",arr);}} />
                    <Form.Control.Feedback type="invalid">{errores.entrevistador_doc}</Form.Control.Feedback>
                  </div>
                </div>
              </div>

              <p className="fw-semibold mb-2 text-muted">Profesional 2 (opcional)</p>
              <div className="border rounded p-3 mb-3 bg-light">
                <div className="row g-2">
                  <div className="col-md-5">
                    <Form.Label>Nombres y apellidos</Form.Label>
                    <Form.Control value={form.entrevistadores[1]?.nombres_apellidos||""} onChange={e=>{const arr=[...form.entrevistadores];arr[1]={...arr[1],nombres_apellidos:e.target.value};f("entrevistadores",arr);}} />
                  </div>
                  <div className="col-md-4">
                    <Form.Label>Profesión / Cargo</Form.Label>
                    <Form.Control value={form.entrevistadores[1]?.profesion_cargo||""} onChange={e=>{const arr=[...form.entrevistadores];arr[1]={...arr[1],profesion_cargo:e.target.value};f("entrevistadores",arr);}} />
                  </div>
                  <div className="col-md-3">
                    <Form.Label>N° documento</Form.Label>
                    <Form.Control value={form.entrevistadores[1]?.numero_documento||""} onChange={e=>{const arr=[...form.entrevistadores];arr[1]={...arr[1],numero_documento:e.target.value};f("entrevistadores",arr);}} />
                  </div>
                </div>
              </div>
                <div className="row g-3 mt-2">
                <div className="col-md-6">
                  <Form.Label>Nombres y apellidos del cuidador / familiar</Form.Label>
                  <Form.Control value={form.cuidador_nombres} onChange={e=>f("cuidador_nombres",e.target.value)} />
                </div>
                <div className="col-md-6">
                  <Form.Label>Número de documento</Form.Label>
                  <Form.Control value={form.cuidador_documento} onChange={e=>f("cuidador_documento",e.target.value)} />
                </div>
                <div className="col-md-6">
                  <Form.Label>Nombres y apellidos del solicitante con discapacidad</Form.Label>
                  <Form.Control value={form.solicitante_nombres} onChange={e=>f("solicitante_nombres",e.target.value)} />
                </div>
                <div className="col-md-6">
                  <Form.Label>Número de documento</Form.Label>
                  <Form.Control value={form.solicitante_documento} onChange={e=>f("solicitante_documento",e.target.value)} />
                </div>
              </div>
            </>
          )}

        </Form>
      </Modal.Body>

      <Modal.Footer>
        {step > 1 && <Button variant="secondary" onClick={prevStep}>Anterior</Button>}
        {step < TOTAL_PASOS ? (
          <Button onClick={nextStep}>Siguiente</Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Guardando..." : persona ? "Actualizar" : "Guardar"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
